import logger from 'src/logger';
import { makePolygonComponent, polygonComponentType } from 'src/sdk-components';
import { SceneComponent } from 'src/sdk-components/SceneComponent';
import { TourObject, VSInteractionType } from 'src/sdk-components/VSObject';
import { TransformationMode } from 'src/service/scene/scene-nodes/TransformControls/data';
import { TSceneNode } from 'src/service/scene/types';
import { MpSdk } from 'src/types';
import { NodeEventsTypes } from '../../enums';
import Scene from '../../scene';
import { SceneMode } from './types';
import { TransformControls } from 'src/service/scene/scene-nodes';

class ScenePolygon extends Scene {
  readonly mpSDK: any;

  readonly assets: any = new Map<string, any[]>();

  readonly customTransformControls: any = [];

  constructor(
    mpSDK: MpSdk,
    private transformControls: any,
  ) {
    super(mpSDK);
    this.mpSDK = mpSDK;
  }

  async init() {
    await this.mpSDK.Scene.register(polygonComponentType, makePolygonComponent);
  }

  async create(data: any): Promise<TSceneNode | undefined> {
    try {
      const modelNode: any = await this.scene.createNode();

      const initialInputs: SceneComponent['inputs'] = {
        colorDefault: data.colorDefault,
        colorHover: data.colorHover,
        colorClick: data.colorClick,
        opacityDefault: data.opacityDefault,
        opacityHover: data.opacityHover,
        opacityClick: data.opacityClick,
        edgesOpacityDefault: data.edgesOpacityDefault,
        edgesOpacityHover: data.edgesOpacityHover,
        edgesOpacityClick: data.edgesOpacityClick,
      };

      const component: any = modelNode.addComponent(polygonComponentType, initialInputs);

      // const inputComponent = config?.isAddingProcess
      //   ? modelNode.addComponent('mp.input', {
      //     eventsEnabled: true,
      //     userNavigationEnabled: false,
      //     unfiltered: false
      //   })
      //   : undefined;

      modelNode.obj3D.position.set(data.positionX, data.positionY, data.positionZ);
      modelNode.obj3D.rotation.set(data.rotationX, data.rotationY, data.rotationZ);
      modelNode.obj3D.scale.set(data.scaleX, data.scaleY, data.scaleZ);

      component.originId = data.id;

      component.onEvent = (type: string, eventData: any) => {
        if (type === VSInteractionType.HOVER) {
          component.handleHover(eventData);
          this.emit(NodeEventsTypes.POLYGON_HOVER, { assetId: data.id, eventData });
        }
        if (type === VSInteractionType.CLICK) {
          component.handleClick();
          this.emit(NodeEventsTypes.POLYGON_CLICK, { assetId: data.id, eventData });
        }

        this.emit(NodeEventsTypes.POLYGON_EVENTS, { type, eventData, component, data });
      };

      await modelNode.start();

      // waiting for all textures to load
      this.assets.set(data.id, [modelNode, component]);

      return [modelNode, component];
    } catch (e) {
      logger.error(e);
    }
    return undefined;
  }

  async changeMode(
    mode: SceneMode,
    node: TSceneNode,
    transformationMode: TransformationMode = TransformationMode.TRANSLATE,
  ) {
    const [modelNode] = node;

    if (mode === SceneMode.Edit) {
      // Make the transform control visible so that the user can manipulate the control selection.
      this.transformControls.control.outputs.objectRoot.visible = true;
      // The three.js object being controlled by this component.
      this.transformControls.control.inputs.selection = modelNode;
      // The transformation mode. Valid values are ‘translate’, ‘rotate’ and ‘scale’.
      this.transformControls.control.inputs.mode = transformationMode;
    } else {
      this.transformControls.control.outputs.objectRoot.visible = false;
      this.transformControls.control.inputs.selection = null;
      this.transformControls.control.inputs.mode = transformationMode;
    }
  }

  async initCustomTransformControls() {
    const transformControls = new TransformControls(this.mpSDK);
    await transformControls.create();
    this.customTransformControls.push(transformControls);
    return transformControls;
  }

  async changeModeCustom(
    mode: SceneMode,
    node: TSceneNode,
    transformControls: TransformControls,
    transformationMode: TransformationMode = TransformationMode.TRANSLATE,
  ) {
    const [modelNode] = node;

    if (mode === SceneMode.Edit) {
      // Make the transform control visible so that the user can manipulate the control selection.
      transformControls.control.outputs.objectRoot.visible = true;
      // The three.js object being controlled by this component.
      transformControls.control.inputs.selection = modelNode;
      // The transformation mode. Valid values are ‘translate’, ‘rotate’ and ‘scale’.
      transformControls.control.inputs.mode = transformationMode;
    } else {
      transformControls.control.outputs.objectRoot.visible = false;
      transformControls.control.inputs.selection = null;
      transformControls.control.inputs.mode = transformationMode;
    }
  }

  async remove(data: TourObject) {
    if (this.assets.has(data.id)) {
      const [modelNode] = this.assets.get(data.id);
      if (!modelNode) return;
      modelNode.stop();
    }
  }
}

export default ScenePolygon;
